export interface AccountResponse {
    id: number;
    userId: number;
    accountType: AccountTypeEnum;
    financeCompanyId: number | null;
    name: string;
    description: string | null;
    currency: string;
    amount: number | null;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
    financeCompanyName: string | null;
    hasNotes: boolean;
    stockPositionLots: StockPositionLotResponse[] | null;
}

export interface StockPositionLotResponse {
    id: number;
    stockPositionId: number;
    accountId: number;
    quantity: number;
    remainingQuantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    buyTotalExchangeRateId: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    stockTicker: string | null;
    stockName: string | null;
}


export enum AccountTypeEnum {
    Cash = 1,
    InvestingForStocks = 2,
    InvestingForCrypto = 3,
    Other = 4,
    Fund = 5
}


export interface AddAttachmentRequest {
    noteId: number;
    url: string | null;
    contentType: string | null;
    fileName: string | null;
    sizeBytes: number | null;
    binaryBase64: string | null;
}


export interface ApiResponse<T> {
    success: boolean;
    message: string | null;
    errorCode: number | null;
    data: T | null;
}


export interface AuthResponse {
    token: string;
    userId: number;
    name: string;
    email: string;
}


export interface CoinPositionResponse {
    id: number;
    userId: number;
    coinTickerId: number;
    symbol: string;
    coinName: string;
    quantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    lots: CoinPositionLotResponse[];
    soldQuantity: number;
}

export interface CoinPositionLotResponse {
    id: number;
    userId: number;
    coinPositionId: number;
    buyTransactionId: number | null;
    accountId: number;
    quantity: number;
    remainingQuantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    closedAt: string | null;
    account: CoinPositionLotAccountResponse | null;
}

export interface CoinPositionLotAccountResponse {
    id: number;
    name: string;
    accountType: AccountTypeEnum;
    currency: string;
}


export interface CoinTickerResponse {
    id: number;
    symbol: string;
    name: string | null;
    createdDate: string;
}


export interface CoinTransactionResponse {
    id: number;
    userId: number;
    coinTickerId: number;
    symbol: string | null;
    coinName: string | null;
    transactionId: number | null;
    accountId: number;
    accountName: string | null;
    coinPositionLotId: number;
    buyOrSell: string;
    date: string;
    count: number;
    coinPrice: number;
    averagePrice: number;
    commission: number | null;
    amount: number;
    amountInUsd: number;
    description: string | null;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
}


export interface GetRequest {
    id: number;
}

export interface DeleteRequest {
    id: number;
    description: string | null;
}


export interface CreateAccountFixRequest {
    accountId: number;
    newAmount: number;
    description: string | null;
    date: string;
}


export interface CreateAccountRequest {
    accountType: AccountTypeEnum;
    financeCompanyId: number | null;
    name: string;
    description: string | null;
    currency: string;
}


export interface CreateBackupRequest {
    backupDirectory: string | null;
    fileName: string | null;
    withCompression: boolean;
}


export interface CreateCoinBuyTransactionRequest {
    coinTickerId: number;
    toAccountId: number;
    date: string;
    count: number;
    coinPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateCoinSellTransactionRequest {
    coinTickerId: number;
    fromAccountId: number;
    date: string;
    count: number;
    coinPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateCoinTickerRequest {
    symbol: string;
    name: string | null;
}


export interface CreateExpenseRequest {
    fromAccountId: number;
    amount: number;
    description: string | null;
    date: string;
}


export interface CreateFinanceCompanyRequest {
    name: string;
    description: string | null;
    logoBinaryBase64: string | null;
}


export interface CreateFirstInitRequest {
    toAccountId: number;
    amount: number;
    description: string | null;
    date: string;
}


export interface CreateForeignExchangeRequest {
    fromAccountId: number;
    toAccountId: number;
    fromAmount: number;
    toAmount: number;
    description: string | null;
    date: string;
}


export interface CreateFundBuyTransactionRequest {
    fundTickerId: number;
    toAccountId: number;
    date: string;
    count: number;
    fundPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateFundSellTransactionRequest {
    fundTickerId: number;
    fromAccountId: number;
    date: string;
    count: number;
    fundPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateFundTickerRequest {
    code: string;
    name: string | null;
    currency: string;
}


export interface CreateIncomeRequest {
    toAccountId: number;
    amount: number;
    description: string | null;
    date: string;
}


export interface CreateNoteRequest {
    ownerType: NoteOwnerType;
    ownerId: number;
    noteType: NoteType;
    title: string | null;
    content: string;
    contextType: NoteContextType;
    snapshotPrice: number | null;
    snapshotAmount: number | null;
    snapshotCurrency: string | null;
    snapshotValueInUsd: number | null;
    snapshotExchangeRateId: number | null;
    snapshotAsOf: string | null;
    snapshotSource: string | null;
    attachments: NoteAttachmentDto[] | null;
}

export interface NoteAttachmentDto {
    url: string;
    contentType: string | null;
    fileName: string | null;
    sizeBytes: number | null;
}


export interface CreateOptionBuyTransactionRequest {
    stockTickerId: number;
    optionContractId: number | null;
    code: string | null;
    multiplier: number;
    currency: string;
    right: number | null;
    strike: number | null;
    expiry: DateOnly | null;
    toAccountId: number;
    date: string;
    count: number;
    optionPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateOptionSellTransactionRequest {
    optionContractId: number;
    code: string | null;
    right: number | null;
    strike: number | null;
    expiry: DateOnly | null;
    fromAccountId: number;
    date: string;
    count: number;
    optionPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreatePortfolioSnapshotRequest {
    date: string;
    manualPrices?: ManualPriceEntry[] | null;
}


export interface CreateStockBuyTransactionRequest {
    stockTickerId: number;
    toAccountId: number;
    date: string;
    count: number;
    stockPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateStockSellTransactionRequest {
    stockTickerId: number;
    fromAccountId: number;
    date: string;
    count: number;
    stockPrice: number;
    commission: number | null;
    description: string | null;
}


export interface CreateStockTickerRequest {
    ticker: string;
    stockMarketShortName: string;
    name: string;
    currency: string;
}


export interface CreateTransactionRequest {
    fromAccountId: number;
    toAccountId: number;
    amount: number;
    description: string | null;
    date: string;
    commission: number | null;
}


export interface DeleteAccountRequest {
    id: number;
    deletedDescription: string;
}


export interface DeleteFinanceCompanyRequest {
    id: number;
    deletedDescription: string;
}


export interface DeleteOptionBuyRequest {
    optionTransactionId: number;
    deletedDescription: string | null;
}


export interface DeleteOptionSellRequest {
    transactionId: number;
    deletedDescription: string | null;
}


export interface DeletePortfolioSnapshotRequest {
    id: number;
}


export interface DeleteStockBuyRequest {
    stockTransactionId: number;
    deletedDescription: string | null;
}


export interface DeleteStockSellRequest {
    transactionId: number;
    deletedDescription: string | null;
}


export interface DeleteStockTickerRequest {
    id: number;
    description: string | null;
}


export interface ExchangeRateResponse {
    id: number;
    baseCurrency: string;
    targetCurrency: string;
    rate: number;
    sourceName: string;
    dateOfValue: DateOnly;
    createdDate: string;
}


export interface FinanceCompanyResponse {
    id: number;
    userId: number | null;
    name: string;
    description: string | null;
    hasLogo: boolean;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
}


export interface FundLastPriceResponse {
    code: string;
    name: string | null;
    date: DateOnly;
    price: number;
}


export interface FundPositionResponse {
    id: number;
    userId: number;
    fundTickerId: number;
    code: string;
    fundName: string;
    quantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    lots: FundPositionLotResponse[];
    soldQuantity: number;
}

export interface FundPositionLotResponse {
    id: number;
    userId: number;
    fundPositionId: number;
    buyTransactionId: number | null;
    accountId: number;
    quantity: number;
    remainingQuantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    closedAt: string | null;
    account: FundPositionLotAccountResponse | null;
}

export interface FundPositionLotAccountResponse {
    id: number;
    name: string;
    accountType: AccountTypeEnum;
    currency: string;
}


export interface FundTickerResponse {
    id: number;
    code: string;
    name: string | null;
    currency: string;
    createdDate: string;
}


export interface FundTransactionResponse {
    id: number;
    userId: number;
    fundTickerId: number;
    code: string | null;
    fundName: string | null;
    transactionId: number | null;
    accountId: number;
    accountName: string | null;
    fundPositionLotId: number;
    buyOrSell: string;
    date: string;
    count: number;
    fundPrice: number;
    averagePrice: number;
    commission: number | null;
    amount: number;
    amountInUsd: number;
    description: string | null;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
}


export interface GetAccountRequest {
    id: number;
}


export interface GetAccountsRequest extends GetAllBaseRequest {
    includeDeleted: boolean;
}


export interface GetAllBaseRequest {
    pageNumber: number;
    pageSize: number;
    sortBy: string | null;
    sortDesc: boolean | null;
    searchTerm: string | null;
}


export interface GetCoinTickerRequest {
    id: number;
}


export interface GetFinanceCompaniesRequest extends GetAllBaseRequest {
    includeDeleted: boolean;
}


export interface GetFinanceCompanyRequest {
    id: number;
}


export interface GetFundLastPriceRequest {
    code: string;
    lookbackDays: number;
}


export interface GetNotesRequest extends GetAllBaseRequest {
    ownerType: NoteOwnerType | null;
    ownerId: number | null;
    noteType: NoteType | null;
    tags: string[] | null;
    from: string | null;
    to: string | null;
    includeDeleted: boolean;
}


export interface GetOptionPriceRequest {
    symbol: string;
    expiration: DateOnly;
    optionType: OptionRightEnum;
    strike: number;
}


export interface GetOptionTransactionsRequest {
    pageNumber: number;
    pageSize: number;
    sortBy: string | null;
    sortDesc: boolean | null;
    searchTerm: string | null;
    stockTickerId: number | null;
    optionContractId: number | null;
    accountId: number | null;
    buyOrSell: string | null;
    fromDate: string | null;
    toDate: string | null;
    includeDeleted: boolean;
}


export interface GetPortfolioSnapshotHistoryRequest {
    startDate: string;
    endDate: string;
}


export interface GetPortfolioSnapshotRequest {
    date: string;
}


export interface GetSameDayTransactionsRequest {
    accountId: number;
    date: string;
    includeDeleted: boolean;
}


export interface GetStockPositionRequest {
    id: number;
}


export interface GetStockPositionsRequest {
    includeClosed: boolean;
    sortBy: string | null;
    sortDescending: boolean;
}


export interface GetStockPriceRequest {
    symbol: string;
}


export interface GetStockPriceResponse {
    symbol: string;
    name: string;
    regularMarketPrice: number;
    regularMarketDayHigh: number;
    regularMarketDayLow: number;
    regularMarketOpen: number;
    regularMarketPreviousClose: number;
    regularMarketVolume: number;
    currency: string;
    exchange: string;
}


export interface GetStockTickerRequest {
    id: number;
}


export interface GetStockTransactionsRequest {
    pageNumber: number;
    pageSize: number;
    sortBy: string | null;
    sortDesc: boolean | null;
    searchTerm: string | null;
    stockTickerId: number | null;
    accountId: number | null;
    buyOrSell: string | null;
    fromDate: string | null;
    toDate: string | null;
    includeDeleted: boolean;
}


export interface GetTransactionRequest {
    id: number;
}


export interface GetTransactionsRequest extends GetAllBaseRequest {
    includeDeleted: boolean;
    accountId: number | null;
    transactionType: number | null;
    transactionTypes: number[] | null;
    accountNameFilter: string | null;
}


export interface ListBackupsRequest {
    backupDirectory: string | null;
}


export interface ListBackupsResponse {
    filePath: string;
    sizeBytes: number | null;
    backupFinishDate: string | null;
    backupType: string | null;
}


export interface LoginRequest {
    email: string;
    password: string;
}


export interface LongDescriptionAttribute extends Attribute {
    longDescription: string;
}


export interface ManualPriceEntry {
    ticker: string;
    assetType: string;
    price: number;
}


export interface MissingPriceInfo {
    ticker: string;
    assetType: string;
    currency: string;
}


export interface NoteAttachmentMetaResponse {
    id: number;
    url: string;
    contentType: string | null;
    fileName: string | null;
    sizeBytes: number | null;
    createdDate: string;
}


export enum NoteOwnerType {
    Account = 1,
    Transaction = 2,
    StockTicker = 3,
    StockPosition = 4,
    StockPositionLot = 5,
    StockTransaction = 6,
    CoinTicker = 7,
    CoinPosition = 8,
    CoinPositionLot = 9,
    CoinTransaction = 10,
    FundTicker = 11,
    FundPosition = 12,
    FundPositionLot = 13,
    FundTransaction = 14
}

export enum NoteType {
    General = 1,
    Research = 2,
    Idea = 3,
    Warning = 4,
    Journal = 5,
    Todo = 6,
    Compliance = 7
}

export enum NoteContextType {
    Other = 0,
    InstrumentPrice = 1,
    AccountBalance = 2,
    TransactionAmount = 3,
    PositionMarketValue = 4
}


export interface NoteResponse {
    id: number;
    userId: number;
    ownerType: NoteOwnerType;
    ownerId: number;
    noteType: NoteType;
    title: string | null;
    tagsText: string | null;
    contextType: NoteContextType;
    snapshotPrice: number | null;
    snapshotAmount: number | null;
    snapshotCurrency: string | null;
    snapshotValueInUsd: number | null;
    snapshotExchangeRateId: number | null;
    snapshotAsOf: string | null;
    snapshotSource: string | null;
    createdDate: string;
    updatedDate: string | null;
    attachments: NoteAttachmentMetaResponse[];
}


export interface OptionPositionResponse {
    id: number;
    userId: number;
    optionContractId: number;
    code: string | null;
    stockTickerId: number;
    ticker: string | null;
    right: OptionRightEnum | null;
    strike: number;
    expiry: DateOnly;
    multiplier: number;
    currency: string;
    quantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    lots: OptionPositionLotResponse[];
    soldQuantity: number;
}

export interface OptionPositionLotResponse {
    id: number;
    userId: number;
    optionPositionId: number;
    buyTransactionId: number | null;
    accountId: number;
    quantity: number;
    remainingQuantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    closedAt: string | null;
    account: OptionPositionLotAccountResponse | null;
}

export interface OptionPositionLotAccountResponse {
    id: number;
    name: string;
    accountType: AccountTypeEnum;
    currency: string;
}


export interface OptionPriceResponse {
    found: boolean;
    rawOutput: string;
    message: string | null;
    symbol: string | null;
    expiration: DateOnly | null;
    optionType: OptionRightEnum | null;
    strike: number | null;
    matchedStrike: number | null;
    strikeDiff: number | null;
    lastPrice: number | null;
    bid: number | null;
    ask: number | null;
    volume: number | null;
    openInterest: number | null;
    impliedVolatility: number | null;
    isInTheMoney: boolean | null;
}


export enum OptionRightEnum {
    Call = 1,
    Put = 2
}


export interface OptionTransactionResponse {
    id: number;
    userId: number;
    optionContractId: number;
    stockTickerId: number;
    code: string | null;
    accountId: number;
    accountName: string | null;
    optionPositionLotId: number | null;
    buyOrSell: string | null;
    date: string;
    count: number;
    optionPrice: number;
    averagePrice: number;
    commission: number | null;
    amount: number;
    amountInUsd: number;
    description: string | null;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
    ticker: string | null;
}


export interface PagedResponse<TItem> {
    items: TItem[];
    pageNumber: number;
    pageSize: number;
    totalCount: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
}


export interface PortfolioSnapshotAccountResponse {
    id: number;
    accountId: number;
    accountName: string;
    currency: string;
    accountType: AccountTypeEnum;
    financeCompanyName: string | null;
    cashAmount: number;
    cashAmountInUsd: number;
    stockValue: number;
    stockValueInUsd: number;
    totalValue: number;
    totalValueInUsd: number;
    profitLoss: number;
    profitLossInUsd: number;
    profitLossPercentage: number;
    createdDate: string;
}


export interface PortfolioSnapshotDetailResponse {
    id: number;
    portfolioSnapshotId: number;
    accountId: number | null;
    stockPositionId: number | null;
    assetType: string;
    currency: string;
    amount: number;
    amountInUsd: number;
    averagePrice: number | null;
    currentPrice: number | null;
    profitLoss: number | null;
    profitLossInUsd: number | null;
    profitLossPercentage: number | null;
    createdDate: string;
    ticker: string;
    currentPriceDate: DateOnly | null;
}


export interface PortfolioSnapshotResponse {
    id: number;
    userId: number;
    snapshotDate: string;
    totalValue: number;
    totalValueInUsd: number;
    totalCash: number;
    totalCashInUsd: number;
    totalStockValue: number;
    totalStockValueInUsd: number;
    createdDate: string;
    accounts: PortfolioSnapshotAccountResponse[];
    details: PortfolioSnapshotDetailResponse[];
}


export interface RecreateSnapshotsRequest {
    startDate: string;
}


export interface RegisterRequest {
    name: string;
    email: string;
    password: string;
}


export interface RestoreBackupRequest {
    backupFilePath: string;
    restoreToDatabaseName: string | null;
    overwrite: boolean;
    dataFileLogicalName: string | null;
    logFileLogicalName: string | null;
    dataFilePhysicalPath: string | null;
    logFilePhysicalPath: string | null;
}


export interface SearchCoinTickerRequest {
    query: string;
}


export interface SearchStockTickerRequest {
    query: string;
}


export interface StockPositionResponse {
    id: number;
    userId: number;
    stockTickerId: number;
    ticker: string;
    stockName: string;
    quantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    lots: StockPositionLotResponse[];
    soldQuantity: number;
    hasNotes: boolean;
}

export interface StockPositionLotResponse {
    id: number;
    userId: number;
    stockPositionId: number;
    buyTransactionId: number | null;
    accountId: number;
    quantity: number;
    remainingQuantity: number;
    averagePrice: number;
    averagePriceInUsd: number;
    buyTotal: number;
    buyTotalInUsd: number;
    buyTotalExchangeRateId: number | null;
    soldTotal: number | null;
    soldTotalInUsd: number | null;
    commissionTotal: number;
    commissionTotalInUsd: number;
    isOpen: boolean;
    createdDate: string;
    updatedDate: string;
    closedAt: string | null;
    account: StockPositionLotAccountResponse | null;
}

export interface StockPositionLotAccountResponse {
    id: number;
    name: string;
    accountType: AccountTypeEnum;
    currency: string;
}


export interface StockTickerResponse {
    id: number;
    ticker: string;
    stockMarketShortName: string;
    name: string;
    createdDate: string;
    currency: string;
}


export interface StockTransactionResponse {
    id: number;
    userId: number;
    stockTickerId: number;
    ticker: string;
    stockName: string | null;
    transactionId: number | null;
    accountId: number;
    accountName: string;
    stockPositionLotId: number | null;
    exchangeRateId: number | null;
    buyOrSell: string;
    date: string;
    count: number;
    stockPrice: number;
    averagePrice: number;
    commission: number | null;
    amount: number;
    amountInUsd: number;
    description: string | null;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
}


export interface TagSuggestRequest {
    prefix: string;
    limit: number;
    includeDeleted: boolean;
}


export interface TransactionResponse {
    id: number;
    userId: number;
    accountId: number;
    transactionGroupId: string | null;
    relatedTransactionId: number | null;
    transactionType: TransactionTypeEnum;
    amount: number;
    amountUsd: number;
    afterAmount: number;
    afterAmountUsd: number;
    description: string | null;
    date: string;
    createdDate: string;
    deletedDate: string | null;
    deletedDescription: string | null;
    exchangeRateId: number | null;
    commission: number | null;
    calculatedExchangeRate: number | null;
    account: AccountResponse | null;
    exchangeRate: ExchangeRateResponse | null;
    relatedTransaction: TransactionResponse | null;
    stockTransactions: StockTransactionResponse[] | null;
    coinTransactions: CoinTransactionResponse[] | null;
    fundTransactions: FundTransactionResponse[] | null;
    optionTransactions: OptionTransactionResponse[] | null;
}


export enum TransactionTypeEnum {
    FirstInit,
    AccountFix,
    Expense,
    Income,
    Transfer,
    ForeignExchange,
    BuyStock,
    SellStock,
    DeleteBuyStock,
    DeleteSellStock,
    BuyCoin,
    SellCoin,
    BuyFund,
    SellFund,
    BuyOption,
    SellOption
}


export interface UpdateAccountRequest {
    id: number;
    financeCompanyId: number | null;
    name: string;
    description: string | null;
}


export interface UpdateFinanceCompanyRequest {
    id: number;
    name: string;
    description: string | null;
    logoBinaryBase64: string | null;
}


export interface UpdateNoteRequest {
    id: number;
    noteType: NoteType | null;
    title: string | null;
    content: string | null;
    contextType: NoteContextType | null;
    snapshotPrice: number | null;
    snapshotAmount: number | null;
    snapshotCurrency: string | null;
    snapshotValueInUsd: number | null;
    snapshotExchangeRateId: number | null;
    snapshotAsOf: string | null;
    snapshotSource: string | null;
}


export interface UpdateTransactionRequest {
    id: number;
    amount: number;
    description: string | null;
    date: string;
    toAmount: number | null;
    deletedDate: string | null;
    deletedDescription: string | null;
}


